import React, { useState } from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { Person as PersonIcon, Warning as WarningIcon } from '@mui/icons-material'
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { pluralize, titleize } from 'inflection'

import { annotationTypes as defaultAnnotationTypes } from '~/src/Annotations/constants'
import { useAllowedAnnotationTypes } from '~/src/Annotations/utils'
import { getUsers } from '~/src/Journal/utils'
import createLogger from '~/src/Lib/Logging'
import { dynamicPropType, idPropType } from '~/src/Lib/PropTypes'
import { initials, memoize, useControlledState } from '~/src/Lib/Utils'
import BatchIcon from '~/src/UI/Icons/Batch'
import Button from '~/src/UI/Shared/Button'
import SharedIcon from '~/src/UI/Shared/Icon'

const displayName = 'Journal/Filters'
const logger = createLogger(displayName)

const FILTER_TOGGLE_DEBOUNCE_INTERVAL = 1000

const iconSubstitutions = {
  harvestPhase: BatchIcon,
  notification: WarningIcon,
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.dark,
    color: theme.palette.gray,
    padding: theme.spacing(0, 0, 1, 1),
    '& $toggle': {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      height: theme.spacing(4),
    },
    '& $toggleSelected': {
      color: theme.palette.primary.main,
      '&$harvestPhase': {
        color: theme.palette.batch.main,
      },
      '&$lightSchedule': {
        color: theme.palette.plantscore.four,
      }
    },
    '& > span[class*=caption]': {
      display: 'block',
      flex: '0 0 100%',
      paddingLeft: theme.spacing(),
    }
  },
  toggle: {},
  toggleSelected: {},
  harvestPhase: {},
  lightSchedule: {},
  assignee: {
    padding: theme.spacing(),
    '& [class*=MuiAvatar-root]': {
      width: theme.spacing(4.5),
      height: theme.spacing(4.5),
      padding: theme.spacing(1.5, 0, 1),
      fontSize: '1rem',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        background: theme.palette.primary.light,
      }
    },
    '&$active': {
      padding: 0,
      margin: theme.spacing(0.5),
    }
  },
  active: {
    background: theme.palette.primary.main,
    color: theme.palette.text.primary,
    '&:hover': {
      background: theme.palette.primary.light,
    }
  },
  caption: {
    paddingTop: theme.spacing(0.5),
  }
}), { name: displayName })

const getClickCallback = (() => {
  let debounce = null
  return memoize((type, { debounceInterval, dispatch, activeTypesKey, onFilterChange }, setActive) => () => {
    clearTimeout(debounce)
    setActive(oldActive => {
      const newActive = oldActive.includes(type)
        ? oldActive.filter(t => t !== type)
        : [...oldActive, type]
      return newActive.length ? newActive : oldActive
    })
    debounce = setTimeout(() => {
      debounce = null
      setActive(currentActive => {
        dispatch({ type: activeTypesKey, payload: currentActive })
        if (typeof onFilterChange === 'function') {
          onFilterChange(currentActive)
        }
        return currentActive
      })
    }, debounceInterval)
  })
})()

export const FiltersComponent = ({
  caption = 'Filters',
  dispatch,
  allowedTypes,
  annotationTypes = defaultAnnotationTypes,
  activeUserKey = 'activeUser',
  activeTypesKey = 'activeTypes',
  debounceInterval = FILTER_TOGGLE_DEBOUNCE_INTERVAL,
  ...props
}) => {
  const classes = useStyles(props)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const { [activeUserKey]: activeUser } = props
  const [active, setActive] = useControlledState(props, activeTypesKey)
  const systemAllowedTypes = useAllowedAnnotationTypes()
  const finalAnnotationsTypes = annotationTypes.filter(config => systemAllowedTypes.has(config.type))
  logger.debug('final annotation types', {
    annotationTypes,
    finalAnnotationsTypes,
    systemAllowedTypes
  })
  const connectedProps = useConnect('selectCurrentFacility', 'selectMemberships', 'selectUsers')

  return (
    <div className={classes.root}>
      {caption && (
        <Typography variant="caption" className={classes.caption}>
          {caption}
        </Typography>
      )}
      <ToggleButtonGroup size="small" value={active}>
        {finalAnnotationsTypes.map(({ type, filterLabel, label, Icon, noFilter }) => {
          const selected = active.includes(type)

          return noFilter ? null : (
            <Tooltip
              key={type}
              title={`${selected ? i18n`Hide` : i18n`Show`} ${filterLabel ?? pluralize(titleize(typeof label === 'string' ? label : type))}`}
              placement="top"
            >
              <ToggleButton
                color="primary"
                classes={{ root: classes.toggle, selected: classNames(classes.toggleSelected, classes[type]) }}
                disabled={!allowedTypes.includes(type)}
                selected={selected}
                value={type}
                onClick={getClickCallback(type, { debounceInterval, dispatch, activeTypesKey, ...props }, setActive)}
              >
                <SharedIcon
                  color="inherit"
                  fontSize="small"
                  icon={iconSubstitutions[type] ?? Icon}
                />
              </ToggleButton>
            </Tooltip>
          )
        })}
      </ToggleButtonGroup>
      <Tooltip placement="top" title={i18n`Filter by related user`}>
        <IconButton
          color="inherit"
          className={classNames(classes.assignee, activeUser && classes.active)}
          onClick={({ currentTarget }) => {
            setMenuAnchor(oldAnchor => (oldAnchor ? null : currentTarget))
          }}
          size="large"
        >
          {activeUser ? (
            <Avatar>{initials(activeUser.fullName)}</Avatar>
          ) : (
            <SharedIcon
              color="inherit"
              fontSize="small"
              icon={PersonIcon}
            />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {activeUser ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              dispatch({ type: activeUserKey, payload: null })
            }}
            alignItems="center"
          >
            <Button fullWidth size="small" variant="text">{i18n`Clear selection`}</Button>
          </MenuItem>
        ) : null}
        {getUsers(connectedProps).map(user => (
          <MenuItem
            key={user.id}
            onClick={() => {
              setMenuAnchor(null)
              dispatch({ type: activeUserKey, payload: oldUser => (oldUser === user ? null : user) })
            }}
            selected={activeUser === user}
          >
            {user.fullName}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
FiltersComponent.propTypes = {
  allowedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  annotationTypes: PropTypes.arrayOf(PropTypes.shape({
    schema: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.node,
    icon: PropTypes.elementType,
  })),
  // eslint-disable-next-line react/no-unused-prop-types
  activeTypesKey: dynamicPropType(PropTypes.string, PropTypes.arrayOf(PropTypes.string)),
  activeUserKey: dynamicPropType(PropTypes.string, PropTypes.oneOfType([
    PropTypes.symbol,
    PropTypes.shape({
      id: idPropType,
      fullName: PropTypes.string,
    })
  ])),
  caption: PropTypes.node,
  debounceInterval: PropTypes.number,
  dispatch: PropTypes.func.isRequired,
}

export default React.memo(FiltersComponent)
